import Phaser from 'phaser';
import eventsCenter from './EventsCenter';
import { Inventory } from './UI/Inventory';
import MainScene from 'src/app/Scenes/MainScene';
import { BulletGroup } from 'src/classes/SlingShot';
import { Button } from './Buttons/Button';
import VirtualJoyStick from 'phaser3-rex-plugins/plugins/virtualjoystick';
import { onStartInteractionsMapping, getSortedBodies, getCollisionEventKey, onActiveInteractionsMapping, onEndInteractionsMapping } from './InteractionHandler';
import BaseLevelScene from 'src/app/Scenes/BaseLevelScene';
import { BaseSpriteInterface } from './SpriteInterfaces';
import MazeScene from 'src/app/Scenes/MazeScene';
import { Peer } from './Peer';
import { HomePage } from 'src/app/home/home.page';
import { WebsocketService } from 'src/app/services/websocket.service';
import { SlingshotEvent } from 'infrastructure/lib/src/models/slingshot-event';
import { DropItemEvent } from 'infrastructure/lib/src/models/drop-item-event';
import { showToast } from './Utilities';

type JoystickState = {
  force: number;
  angle: number;
}

export class Player extends Phaser.Physics.Matter.Sprite {
  public cursors: Phaser.Types.Input.Keyboard.CursorKeys;
  private maxVelocity: number = 10;
  private _position: Phaser.Math.Vector2;
  public static disableJoystick: boolean = false;
  health: integer = 10;
  sound: Phaser.Sound.BaseSound;
  public actionPressed: boolean = false;
  inventory: Inventory;
  public bulletGroup: BulletGroup;
  cursorKeys: Phaser.Types.Input.Keyboard.CursorKeys;
  private previousJoystickState: JoystickState = {force: 0, angle: 0};
  public gpsX: number;
  public gpsY: number;
  public onWater = false;
  peersInRange: Peer[] = [];

  constructor(data: BaseSpriteInterface, scale=1) {
    let { scene, x, y, texture, frame } = data;
    super(scene.matter.world, x, y, texture, frame);
    this.scale = scale;
    // Load the saved inventory
    Inventory.loadInventory();

    // Setup position
    this._position = new Phaser.Math.Vector2(this.x, this.y);

    // Setup collider and sensor
    const Bodies = new Phaser.Physics.Matter.MatterPhysics(scene).bodies;
    var playerCollider = Bodies.circle(this.x, this.y, 20*this.scale, {
      isSensor: false,
      label: 'playerCollider',
    });
    var playerSensor = Bodies.circle(this.x, this.y, 40*this.scale, {
      isSensor: true,
      label: 'playerSensor',
    });

    this.setMass(100);
    this.setBounce(0.99);

    // Increase the Matter Engine's Time Step Iterations
    this.scene.matter.world.engine.positionIterations = 14; // default is 6
    this.scene.matter.world.engine.velocityIterations = 14; // default is 4

    // Combine into a compound body and apply set on player
    const Body = new Phaser.Physics.Matter.MatterPhysics(scene).body;
    const compoundBody = Body.create({
      parts: [playerCollider, playerSensor],
      frictionAir: 0.35,
      mass: 100,
    });
    this.setExistingBody(compoundBody);
    this.setFixedRotation();

    this.scene.add.existing(this);
    this.setDepth(1);

    // setup keyboard controls
    this.cursorKeys = this.scene.input.keyboard.createCursorKeys();
    this.scene.input.keyboard.disableGlobalCapture();

    // Setup collisions
    this.createCollisions();

    // Add sound for when player is hit
    this.sound = scene.sound.add('playerHit');

    // setup bullets group
    this.bulletGroup = new BulletGroup(this.scene);

    eventsCenter.on('actionButtonPressed', async () => {
      this.actionPressed = true;
      if (this.peersInRange.length > 0) {
        if(WebsocketService.session){
          HomePage.openPeerConversationModal(this.peersInRange[0].playerName, true);
        }
        else{
          await showToast({message: "You need to register to chat with other players.", duration: 2000});
        }
      }
    });

    // setup cursor key foraging
    this.cursorKeys.space.on('down', async () => {
      this.actionPressed = true;
      if (this.peersInRange.length > 0) {
        if(WebsocketService.session){
          HomePage.openPeerConversationModal(this.peersInRange[0].playerName, true);
        }
        else{
          await showToast({message: "You need to register to chat with other players.", duration: 2000});
        }
      }
    });

    // setup the cursor keys
    this.setupCursorKeySlingshot();

  }

  public static preload(scene: Phaser.Scene) {
    scene.load.atlas(
      'player',
      'assets/sprites/tomi-48x64.png',
      'assets/sprites/tomi-48x64_atlas.json'
    );
    scene.load.animation('player_anims', 'assets/sprites/tomi-48x64_anim.json');
    scene.load.audio('playerHit', 'assets/audio/playerHit.mp3');
  }

  get position() {
    this._position.set(this.x, this.y);
    return this._position;
  }

  get dead() {
    return this.health <= 0;
  }

  update(joyStick: VirtualJoyStick) {
    const joystickForce = joyStick.force;
    const joystickAngle = joyStick.angle;

    // Make player transparent if they are on water
    this.setAlpha(this.onWater ? 0.5 : 1);

    // handle joystick slingshot
    if (Player.disableJoystick || Button.buttonDown || this.cursorKeys.space.isDown) {
      this.setVelocity(0, 0);
      if(Button.buttonDown){
        if((joystickForce == 0 && this.previousJoystickState.force > 0)){
          this.fireSlingshot();
        }
      }
    }
    // handle walking
    else {
      var playerVelocity = new Phaser.Math.Vector2();
      playerVelocity.x = 0;
      playerVelocity.y = 0;

      if(!this.onWater){
        this.setAngle(-(this.scene as MainScene | MazeScene | BaseLevelScene).compassAngle)
        if (this.cursors.left.isDown || this.cursorKeys.left.isDown) {
          !this.anims.isPlaying && this.play('walk-left', true);
          playerVelocity.x = -this.scale*5;
        }
        if (this.cursors.right.isDown || this.cursorKeys.right.isDown) {
          !this.anims.isPlaying && this.play('walk-right', true);
          playerVelocity.x = this.scale*5;
        }
        if (this.cursors.up.isDown || this.cursorKeys.up.isDown) {
          !this.anims.isPlaying && this.play('walk-up', true);
          playerVelocity.y = -this.scale*5;
        }
        if (this.cursors.down.isDown || this.cursorKeys.down.isDown) {
          !this.anims.isPlaying && this.play('walk-down', true);
          playerVelocity.y = this.scale*5;
        }
      }
      else{
        let angle = 0;
        if (this.cursors.left.isDown || this.cursorKeys.left.isDown) {
          !this.anims.isPlaying && this.play('walk-up', true);
          playerVelocity.x = -this.scale*5;
          angle = 270;
        }
        if (this.cursors.right.isDown || this.cursorKeys.right.isDown) {
          !this.anims.isPlaying && this.play('walk-up', true);
          playerVelocity.x = this.scale*5;
          angle = 90;
        }
        if (this.cursors.up.isDown || this.cursorKeys.up.isDown) {
          !this.anims.isPlaying && this.play('walk-up', true);
          playerVelocity.y = -this.scale*5;
          angle = 0
        }
        if (this.cursors.down.isDown || this.cursorKeys.down.isDown) {
          !this.anims.isPlaying && this.play('walk-up', true);
          playerVelocity.y = this.scale*5;
          angle = 180;
        }
        this.setAngle(joystickAngle ? joystickAngle-90-180 : angle);
      }

      let vel: number = this.scale*joystickForce / 10;
      if (vel > this.maxVelocity * this.scale) vel = this.maxVelocity * this.scale;

      if(vel){
        playerVelocity.x = Math.cos((Math.PI * joystickAngle) / 180) * vel;
        playerVelocity.y = Math.sin((Math.PI * joystickAngle) / 180) * vel;
      }
      const waterSpeedScaler = this.onWater ? 0.5 : 1;
      const boostScalar = Inventory.activeEnhancements.includes("speed") ? 1.7 : 1;
      this.setVelocity(playerVelocity.x*waterSpeedScaler*boostScalar, playerVelocity.y*waterSpeedScaler*boostScalar);
    }
    this.previousJoystickState = {force: joystickForce, angle: joystickAngle}
  }

  hit(connectionId: string = "") {
    if(Inventory.activeEnhancements.includes("invincibility")){
      this.flash('blue');
    }
    else{
      this.flash('red');
      this.sound.play();
      const droppedItem = Inventory.randomDrop();
      const droppedItemName = droppedItem ? droppedItem.name : null;

      console.log("You dropped: " + droppedItemName);
      // TODO: Broadcast drop event to other players if live-mode is active and player was hit by a peer.
      if (droppedItemName && droppedItemName.length > 0 && connectionId && WebsocketService.socket && WebsocketService.socket.readyState === WebsocketService.socket.OPEN) {
        // Broadcast the drop event to other players
        WebsocketService.broadcastDropItemEvent(new DropItemEvent({
          itemName: droppedItemName,
          x: this.x,
          y: this.y,
          levelName: this.scene.scene.key
        }));
      }


      const health = Inventory.getItemQuantity('heart');
      if(health === 0 && this.scene.scene.key !== 'mainScene'){
        (this.scene as BaseLevelScene).resetLevel();
      }
    }
  }

  flash(color: 'red' | 'green' | 'blue' = 'red') {
    const hexVal = color === 'red' ? 0xff0000 : color === 'green' ? 0x00ff00 : 0x0000FF;
    this.setTint(hexVal);
    setTimeout(() => {
      this.setTint(hexVal);
    }, 250);
    setTimeout(() => {
      this.setTint(0xffffff);
    }, 250);
    setTimeout(() => {
      this.setTint(hexVal);
    }, 250);
    setTimeout(() => {
      this.setTint(0xffffff);
    }, 250);
  }

  createCollisions() {

    this.scene.matter.world.on('collisionstart', (event) => {
      //  Loop through all of the collision start pairs
      var pairs: Array<Phaser.Types.Physics.Matter.MatterCollisionData> = event.pairs;
      for (var i = 0; i < pairs.length; i++) {   

        const sortedBodies = getSortedBodies(pairs[i]);
        const collisionEventKey: string = getCollisionEventKey(sortedBodies);
        if(collisionEventKey.includes('player') || collisionEventKey.includes('bullet')){
          if(collisionEventKey in onStartInteractionsMapping){
            onStartInteractionsMapping[collisionEventKey](...sortedBodies.map((body) => {return body.gameObject}));
          }
        }
      }
    });


    this.scene.matter.world.on('collisionactive', (event) => {
      // Loop through all of the active collision pairs
      var pairs: Array<Phaser.Types.Physics.Matter.MatterCollisionData> = event.pairs;
      for (var i = 0; i < pairs.length; i++) {   

        const sortedBodies = getSortedBodies(pairs[i]);
        const collisionEventKey: string = getCollisionEventKey(sortedBodies);

        if(collisionEventKey.includes('player') || collisionEventKey.includes('bullet')){
          if(collisionEventKey in onActiveInteractionsMapping){
            onActiveInteractionsMapping[collisionEventKey](...sortedBodies.map((body) => {return body.gameObject}));
          }
        }
      }
      this.actionPressed = false;
    });

    // Define what should happen when a collision ends
    this.scene.matter.world.on('collisionend', (event) => {

      // Loop through all of the collision end pairs
      var pairs: Array<Phaser.Types.Physics.Matter.MatterCollisionData> = event.pairs;
      for (var i = 0; i < pairs.length; i++) {   

        const sortedBodies = getSortedBodies(pairs[i]);
        const collisionEventKey: string = `${getCollisionEventKey(sortedBodies)}_end`;

        if(collisionEventKey.includes('player') || collisionEventKey.includes('bullet')){
          if(collisionEventKey in onEndInteractionsMapping){
            onEndInteractionsMapping[collisionEventKey](...sortedBodies.map((body) => {return body.gameObject}));
          }
        }
      }
    });
  }

  setupCursorKeySlingshot(){
    this.cursorKeys.left.on('down', () => {
      if(this.cursorKeys.space.isDown){
        this.fireSlingshot(0);
      }
    });
    this.cursorKeys.right.on('down', () => {
      if(this.cursorKeys.space.isDown){
        this.fireSlingshot(180);
      }
    });
    this.cursorKeys.up.on('down', () => {
      if(this.cursorKeys.space.isDown){
        this.fireSlingshot(90);
      }
    });
    this.cursorKeys.down.on('down', () => {
      if(this.cursorKeys.space.isDown){
        this.fireSlingshot(270);
      }
    });
  }

  fireSlingshot(cursorKeyAngle: number = -1){

    const amoCount = Inventory.getItemQuantity('brown_rock');

    if(amoCount > 0){
      var velocityMagnitude;
      var previousAngle;
      const maxVelocity = 10;
      
      // this was cause by joystick events
      if(cursorKeyAngle < 0){
        velocityMagnitude = Math.min(this.previousJoystickState.force/7, maxVelocity);
        previousAngle = this.previousJoystickState.angle;
      }
      // this was caused by cursor key events
      else{
        velocityMagnitude = maxVelocity;
        previousAngle = cursorKeyAngle;
      }
  
      // Do the math
      const xComponent = Math.cos((2*Math.PI * (previousAngle + 180)) / 360);
      const yComponent = Math.sin((2*Math.PI * (previousAngle + 180)) / 360);
  
      // get velocity components
      const xVelocity = xComponent*velocityMagnitude;
      const yVelocity = yComponent*velocityMagnitude;
  
      // Bullet should be emitted from outside the player
      const positionalOffsetMagnitude = 35*this.scale;
      const xOrigin = xComponent*positionalOffsetMagnitude + this.position.x;
      const yOrigin = yComponent*positionalOffsetMagnitude + this.position.y;


      // fire
      this.bulletGroup.fireSlingshot(xOrigin, yOrigin, xVelocity, yVelocity, this.scale);

      // Broadcast slingshot event to other players if live-mode is active.
      WebsocketService.broadcastSlingshotEvent(new SlingshotEvent({ x: xOrigin, y: yOrigin, velocityX: xVelocity, velocityY: yVelocity, scale: this.scale, levelName: this.scene.scene.key}));

      // Remove the ammo from the inventory
      Inventory.removeItem('brown_rock');
    }

  }
}
